import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Check, Pencil, Sparkles, Bot } from "lucide-react";
import { formatHsCode, sanitizeHsCode } from "@/hooks/useComplianceLookup";
import type { ComplianceResult } from "@/hooks/useComplianceLookup";
import { cn } from "@/lib/utils";

interface Props {
  currentCode: string;
  compliance: ComplianceResult | undefined;
  disabled?: boolean;
  onAccept: (newCode: string) => void;
}

/**
 * Reusable popover content with HS Code suggestion + manual edit modes.
 * Extracted so it can be composed inside any custom Popover trigger
 * (e.g. AuditPanel's amber warning icon) without duplicating UI/logic.
 */
export function HsCodeSuggestionContent({
  currentCode,
  compliance,
  onAccept,
  onClose,
}: {
  currentCode: string;
  compliance: ComplianceResult | undefined;
  onAccept: (newCode: string) => void;
  onClose: () => void;
}) {
  const [manualMode, setManualMode] = useState(false);
  const [manualValue, setManualValue] = useState(currentCode);

  const cleanCurrent = sanitizeHsCode(currentCode);
  const matched = compliance?.matched_codigo;
  const hasSuggestion = !!matched && matched !== cleanCurrent;

  const handleAcceptSuggestion = () => {
    if (matched) {
      onAccept(matched);
      setManualMode(false);
      onClose();
    }
  };

  const handleManualSave = () => {
    const clean = sanitizeHsCode(manualValue);
    if (clean) {
      onAccept(clean);
    }
    setManualMode(false);
    onClose();
  };

  if (!manualMode) {
    return (
      <div className="space-y-3 p-4">
        {/* Current / AI-extracted */}
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Bot className="h-3.5 w-3.5" />
            <span>Extraído por IA</span>
          </div>
          <Badge variant="outline" className="font-mono text-xs">
            {formatHsCode(currentCode) || "—"}
          </Badge>
        </div>

        {/* Suggestion from arancel */}
        {hasSuggestion && matched && (
          <div className="space-y-1.5 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3">
            <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
              <Sparkles className="h-3.5 w-3.5" />
              <span>Sugerencia del Arancel</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="font-mono text-xs bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 border-amber-300">
                {formatHsCode(matched)}
              </Badge>
            </div>
            {compliance?.descripcion_arancel && (
              <p className="text-[11px] text-muted-foreground leading-snug line-clamp-2">
                {compliance.descripcion_arancel}
              </p>
            )}
            <Button size="sm" className="w-full mt-1" onClick={handleAcceptSuggestion}>
              <Check className="h-3.5 w-3.5 mr-1.5" />
              Aceptar Sugerencia
            </Button>
          </div>
        )}

        {!hasSuggestion && compliance?.descripcion_arancel && (
          <div className="rounded-md border border-primary/20 bg-primary/5 p-3 space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-medium text-primary">
              <Check className="h-3.5 w-3.5" />
              <span>Código encontrado en el Arancel</span>
            </div>
            <p className="text-[11px] text-muted-foreground leading-snug line-clamp-2">
              {compliance.descripcion_arancel}
            </p>
            <Button
              size="sm"
              className="w-full mt-1"
              onClick={() => {
                onAccept(cleanCurrent);
                onClose();
              }}
            >
              <Check className="h-3.5 w-3.5 mr-1.5" />
              Confirmar Código (Validación Humana)
            </Button>
          </div>
        )}

        {!hasSuggestion && !compliance?.descripcion_arancel && cleanCurrent.length === 10 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3 space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
              <Sparkles className="h-3.5 w-3.5" />
              <span>Código no encontrado en el Arancel</span>
            </div>
            <p className="text-[11px] text-muted-foreground leading-snug">
              No hay coincidencia en la base 1881. Confirme manualmente la validez del código.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="w-full mt-1"
              onClick={() => {
                onAccept(cleanCurrent);
                onClose();
              }}
            >
              <Check className="h-3.5 w-3.5 mr-1.5" />
              Confirmar tal cual (Mi responsabilidad)
            </Button>
          </div>
        )}

        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => {
            setManualMode(true);
            setManualValue(currentCode);
          }}
        >
          <Pencil className="h-3.5 w-3.5 mr-1.5" />
          Editar Manualmente
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4">
      <p className="text-xs font-medium">Editar Partida Arancelaria</p>
      <Input
        autoFocus
        value={manualValue}
        onChange={(e) => setManualValue(e.target.value)}
        placeholder="Ej: 8471300000"
        className="font-mono text-sm"
        onKeyDown={(e) => {
          if (e.key === "Enter") handleManualSave();
          if (e.key === "Escape") setManualMode(false);
        }}
      />
      <div className="flex gap-2">
        <Button size="sm" className="flex-1" onClick={handleManualSave}>
          Guardar
        </Button>
        <Button size="sm" variant="outline" onClick={() => setManualMode(false)}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

export function HsCodeSuggestion({ currentCode, compliance, disabled, onAccept }: Props) {
  const [open, setOpen] = useState(false);

  const cleanCurrent = sanitizeHsCode(currentCode);
  const matched = compliance?.matched_codigo;
  const hasSuggestion = !!matched && matched !== cleanCurrent;
  const isIncomplete = cleanCurrent.length > 0 && cleanCurrent.length < 10;
  const showWarning = hasSuggestion || isIncomplete;

  const handleOpenChange = (isOpen: boolean) => {
    if (disabled) return;
    setOpen(isOpen);
  };

  if (!cleanCurrent) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "inline-flex items-center gap-1 font-mono text-xs cursor-pointer rounded px-1 py-0.5 transition-colors",
            "hover:bg-muted/60",
            disabled && "cursor-not-allowed opacity-50"
          )}
        >
          <span>{formatHsCode(currentCode)}</span>
          {showWarning && (
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 animate-pulse" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start" side="bottom">
        <HsCodeSuggestionContent
          currentCode={currentCode}
          compliance={compliance}
          onAccept={onAccept}
          onClose={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}
